import { useInfiniteQuery } from 'react-query';
import { productCacheService } from 'services';
import { ServerStateKeysEnum } from '../constants';
import { ProductsUsingRedisRequestParams } from 'types';
import getConfig from 'next/config';
import { isProductCacheEnabled } from 'utils';

export const useFetchProductsUsingRedis = ({
  searchCriteria,
  filters,
  sortDirection,
  pageSize,
  isDsoStore = false,
  enabled = true,
}: ProductsUsingRedisRequestParams) => {
  const { publicRuntimeConfig } = getConfig();
  const storeRegion = publicRuntimeConfig.STORE_REGION;
  return useInfiniteQuery(
    buildCacheKey({ searchCriteria, filters, sortDirection, isDsoStore }),
    ({ pageParam = 1 }) => {
      return productCacheService.getProductsUsingCache(
        searchCriteria,
        filters,
        sortDirection,
        pageSize,
        pageParam,
        isDsoStore
      );
    },
    {
      getNextPageParam: (lastpage, pages) => {
        if (!lastpage || typeof lastpage.currentPageNumber === 'undefined') {
          return undefined;
        }

        if (lastpage.currentPageNumber === lastpage.totalPages) {
          return undefined;
        } else {
          return lastpage.currentPageNumber + 1;
        }
      },
      staleTime: Infinity,
      keepPreviousData: true,
      enabled:
        enabled &&
        isProductCacheEnabled() &&
        Boolean(
          searchCriteria.taxonSlug ||
            searchCriteria.variantIds ||
            searchCriteria.skus ||
            searchCriteria.name
        ),
    }
  );
};

const buildCacheKey = ({
  searchCriteria,
  filters,
  sortDirection,
  isDsoStore,
}: ProductsUsingRedisRequestParams) => {
  const cacheKey: string[] = [ServerStateKeysEnum.productsCache];

  if (searchCriteria.taxonSlug) {
    cacheKey.push('taxon');
    cacheKey.push(searchCriteria.taxonSlug);
  }

  if (searchCriteria.variantIds) {
    cacheKey.push('variants');
    cacheKey.push(...searchCriteria.variantIds.split(','));
  }

  if (searchCriteria.skus) {
    cacheKey.push('skus');
    cacheKey.push(...searchCriteria.skus.split(','));
  }

  if (searchCriteria.name) {
    cacheKey.push('name');
    cacheKey.push(searchCriteria.name);
  }

  if (sortDirection) {
    cacheKey.push(sortDirection);
  }

  if (filters && filters.length > 0) {
    cacheKey.push('filters');
    cacheKey.push(
      ...filters.map(filter => `${filter.filterKey}:${filter.filterValue}`)
    );
  }

  if (isDsoStore) {
    cacheKey.push('dsoStore');
  }

  return cacheKey;
};
